import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { Icon } from '../../atoms/Icon'
import { ModalProps, ModalVariant } from './Modal.types'
import {
  ModalOverlay,
  ModalContent,
  CloseButton,
  DragHandle,
} from './Modal.styles'

// Simple viewport detection for auto bottom-sheet
const MOBILE_BREAKPOINT = 640
const SWIPE_THRESHOLD = 100 // pixels to swipe before dismissing
const SWIPE_VELOCITY_THRESHOLD = 0.5 // pixels per ms

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  children,
  width,
  maxWidth,
  closeOnOverlayClick = true,
  showCloseButton = true,
  fullScreen = false,
  className,
  variant = 'default',
  autoBottomSheet = false,
  swipeToDismiss = true,
}) => {
  const [isMobile, setIsMobile] = useState(false)
  const [swipeOffset, setSwipeOffset] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const contentRef = useRef<HTMLDivElement>(null)
  const previouslyFocusedRef = useRef<HTMLElement | null>(null)
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose
  const touchStartY = useRef(0)
  const touchStartTime = useRef(0)

  // Detect mobile for auto bottom-sheet
  useEffect(() => {
    if (!autoBottomSheet) return

    const checkMobile = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT)
    }

    checkMobile()
    window.addEventListener('resize', checkMobile)

    return () => window.removeEventListener('resize', checkMobile)
  }, [autoBottomSheet])

  // Determine effective variant
  const effectiveVariant: ModalVariant = useMemo(() => {
    if (autoBottomSheet && isMobile) {
      return 'bottom-sheet'
    }
    return variant
  }, [autoBottomSheet, isMobile, variant])

  const isBottomSheet = effectiveVariant === 'bottom-sheet'
  const enableSwipe = isBottomSheet && swipeToDismiss

  // Reset swipe offset when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSwipeOffset(0)
      setIsDragging(false)
    }
  }, [isOpen])

  // Swipe handlers for bottom-sheet
  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (!enableSwipe) return

      // Only handle swipe from the drag handle area (top 60px)
      const touch = e.touches[0]
      const rect = contentRef.current?.getBoundingClientRect()
      if (!rect) return

      const touchY = touch.clientY - rect.top
      if (touchY > 60) return // Only swipe from top area

      touchStartY.current = touch.clientY
      touchStartTime.current = Date.now()
      setIsDragging(true)
    },
    [enableSwipe]
  )

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (!enableSwipe || !isDragging) return

      const touch = e.touches[0]
      const deltaY = touch.clientY - touchStartY.current

      // Only allow swiping down (positive delta)
      if (deltaY > 0) {
        setSwipeOffset(deltaY)
      }
    },
    [enableSwipe, isDragging]
  )

  const handleTouchEnd = useCallback(() => {
    if (!enableSwipe || !isDragging) return

    const elapsed = Date.now() - touchStartTime.current
    const velocity = swipeOffset / elapsed

    // Dismiss if swiped past threshold or with high velocity
    if (swipeOffset > SWIPE_THRESHOLD || velocity > SWIPE_VELOCITY_THRESHOLD) {
      onClose()
    }

    setSwipeOffset(0)
    setIsDragging(false)
  }, [enableSwipe, isDragging, swipeOffset, onClose])

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    }

    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isOpen])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCloseRef.current()
        return
      }

      if (e.key !== 'Tab' || !contentRef.current) return
      const focusable = Array.from(
        contentRef.current.querySelectorAll<HTMLElement>(
          'button:not(:disabled), [href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])'
        )
      ).filter((element) => !element.hasAttribute('hidden'))

      if (focusable.length === 0) {
        e.preventDefault()
        contentRef.current.focus()
        return
      }

      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }

    if (isOpen) {
      previouslyFocusedRef.current =
        document.activeElement as HTMLElement | null
      document.addEventListener('keydown', handleKeyDown)
      requestAnimationFrame(() => {
        const firstFocusable = contentRef.current?.querySelector<HTMLElement>(
          'button:not(:disabled), [href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])'
        )
        const focusTarget = firstFocusable ?? contentRef.current
        focusTarget?.focus()
      })
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      previouslyFocusedRef.current?.focus()
    }
  }, [isOpen])

  if (!isOpen) return null

  const swipeStyle =
    enableSwipe && swipeOffset > 0
      ? {
          transform: `translateY(${swipeOffset}px)`,
          transition: isDragging ? 'none' : 'transform 0.3s ease-out',
        }
      : undefined

  const modalContent = (
    <ModalOverlay
      $isOpen={isOpen}
      $variant={effectiveVariant}
      onClick={closeOnOverlayClick ? onClose : undefined}
    >
      <ModalContent
        ref={contentRef}
        $width={width}
        $maxWidth={maxWidth}
        $fullScreen={fullScreen}
        $isOpen={isOpen}
        $variant={effectiveVariant}
        className={className}
        onClick={(e) => e.stopPropagation()}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={swipeStyle}
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
      >
        {isBottomSheet && <DragHandle />}
        {showCloseButton && (
          <CloseButton onClick={onClose}>
            <Icon name="close" size="medium" />
          </CloseButton>
        )}
        {children}
      </ModalContent>
    </ModalOverlay>
  )

  return createPortal(modalContent, document.body)
}
